import * as React from 'react';
import { RemoveTeamMemberModalView, RemoveTeamMemberModalViewProps } from './RemoveTeamMemberModal.view';
import { useState } from 'react';
import { useSelectedOrganization } from '../../../hooks/useSelectedOrganization';
import { ApolloError } from '@apollo/client';
import { SettingsTeamMemberStatusEnum } from '../../../constants/team-members';
import { remapApolloErrors } from '../../../helpers/remapApolloErrors';
import { showToast } from '../../../helpers/showToast';
import {
  DeleteInvitedMemberMutationFn,
  PlanUsageType,
  RemoveUserOrganizationAssignmentMutationFn,
  UsageFieldsFragmentDoc
} from '../../../models/graphql';

export type RemoveTeamMemberModalControllerProps = Omit<
  RemoveTeamMemberModalViewProps,
  'selectedOrganization' | 'submitted' | 'setSubmitted' | 'handleSubmit' | 'onSubmit'
> & {
  deleteUserInvitation: DeleteInvitedMemberMutationFn;
  removeUserOrgAssignment: RemoveUserOrganizationAssignmentMutationFn;
};

export const RemoveTeamMemberModalController = (props: RemoveTeamMemberModalControllerProps): JSX.Element => {
  const { selectedOrganization } = useSelectedOrganization();
  const [submitted, setSubmitted] = useState<boolean>(false);

  const onSubmit = () => {
    setSubmitted(true);

    if (props.user.status === SettingsTeamMemberStatusEnum.INVITED) {
      props
        .deleteUserInvitation({
          variables: { userInvitationId: props.user.id },
          update: cache => {
            cache.evict({ id: cache.identify({ id: props.user?.id, __typename: 'UserInvitation' }) });
            cache.gc();
            const oldUsage: PlanUsageType | null = cache.readFragment({
              fragment: UsageFieldsFragmentDoc,
              fragmentName: 'UsageFields',
              id: 'PlanUsageType:{}'
            });
            const newUsage = { ...oldUsage, users: (oldUsage?.users ?? 0) - 1 };
            cache.writeFragment({
              fragment: UsageFieldsFragmentDoc,
              fragmentName: 'UsageFields',
              id: 'PlanUsageType:{}',
              data: newUsage
            });
          }
        })
        .then(() => {
          props.onClose();
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error in revoking the invited user',
            detail: mappedError,
            severity: 'error'
          });
          setSubmitted(false);
        });
    } else if (selectedOrganization) {
      props
        .removeUserOrgAssignment({
          variables: { organizationId: selectedOrganization.id, userId: props.user.id },
          update: cache => {
            cache.evict({ id: cache.identify({ id: props.user?.id, __typename: 'User' }) });
            cache.gc();
          }
        })
        .then(() => {
          props.onClose();
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error in removing the user',
            detail: mappedError,
            severity: 'error'
          });
          setSubmitted(false);
        });
    }
  };

  const viewProps: RemoveTeamMemberModalViewProps = {
    ...props,
    onSubmit,
    submitted,
    selectedOrganization
  };
  return <RemoveTeamMemberModalView {...viewProps} />;
};
